// src/pages/AdminPage.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Flag,
  Check,
  Ban,
  Clock,
  Users,
  AlertTriangle,
  MessageSquarePlus,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import reportService from '../services/reportService';
import userService from '../services/userService';
import feedbackService from '../services/feedbackService';
import { Btn, Card, Badge, Loading, Empty, ErrorState } from '../components/ui';

const REASON_KEYS = {
  inappropriate: 'admin.reasons.inappropriate',
  fake_profile: 'admin.reasons.fake_profile',
  harassment: 'admin.reasons.harassment',
  spam: 'admin.reasons.spam',
  scam: 'admin.reasons.scam',
  wrong_category: 'admin.reasons.wrong_category',
  illegal: 'admin.reasons.illegal',
  other: 'admin.reasons.other',
};

const AdminPage = ({ user, toast }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [profiles, setProfiles] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [expandedFeedback, setExpandedFeedback] = useState(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      try {
        setError(false);
        setLoading(true);
        const [reps, st, fbs] = await Promise.all([
          reportService.getReports(filter),
          reportService.getStats(),
          feedbackService.getFeedbacks(30),
        ]);
        setReports(reps);
        setStats(st);
        setFeedbacks(fbs);

        // Charger les profils référencés
        const uids = new Set();
        reps.forEach((r) => {
          uids.add(r.reporterId);
          uids.add(r.reportedId);
        });
        const profs = { ...profiles };
        for (const uid of uids) {
          if (!profs[uid]) {
            const p = await userService.getProfile(uid);
            if (p) profs[uid] = p;
          }
        }
        setProfiles(profs);
      } catch (err) {
        console.error('AdminPage load error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, filter]);

  if (!user) {
    navigate('/');
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="pt-32 pb-32 container mx-auto px-4">
        <Card className="max-w-md mx-auto p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <Shield size={28} className="text-red-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3 dark:text-gray-100">
            {t('admin.accessDenied.title')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{t('admin.accessDenied.desc')}</p>
          <Btn onClick={() => navigate('/')} className="w-full">
            {t('admin.accessDenied.cta')}
          </Btn>
        </Card>
      </div>
    );
  }

  const handleResolve = async (reportId, resolution) => {
    setActionLoading(reportId);
    const r = await reportService.resolveReport(reportId, resolution);
    setActionLoading(null);
    if (r.success) {
      toast(
        resolution === 'resolved' ? t('admin.toast.resolved') : t('admin.toast.rejected'),
        'success'
      );
      setReports((prev) => prev.filter((rep) => rep.id !== reportId));
      if (stats) {
        setStats({ ...stats, pendingReports: Math.max(0, stats.pendingReports - 1) });
      }
    } else {
      toast(t('admin.toast.error'), 'error');
    }
  };

  const handleFeedbackStatus = async (feedbackId, status) => {
    const r = await feedbackService.updateFeedbackStatus(feedbackId, status);
    if (r.success) {
      setFeedbacks((prev) => prev.map((f) => (f.id === feedbackId ? { ...f, status } : f)));
      toast(t('admin.toast.feedbackUpdated'), 'success');
    } else {
      toast(t('admin.toast.error'), 'error');
    }
  };

  const newFeedbackCount = feedbacks.filter((f) => f.status === 'new').length;

  return (
    <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4 max-w-4xl">
      <Helmet>
        <title>{t('admin.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/admin" />
      </Helmet>
      <div className="mb-6">
        <h1 className="text-2xl font-bold dark:text-gray-100 flex items-center gap-2">
          <Shield size={24} className="text-rose-500" />
          {t('admin.title')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm">{t('admin.subtitle')}</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Card className="p-4 text-center">
            <Users size={20} className="mx-auto mb-1 text-blue-500" />
            <p className="text-xl font-bold dark:text-gray-100">{stats.totalUsers}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.stats.users')}</p>
          </Card>
          <Card className="p-4 text-center">
            <Flag size={20} className="mx-auto mb-1 text-orange-500" />
            <p className="text-xl font-bold dark:text-gray-100">{stats.totalReports}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.stats.reports')}</p>
          </Card>
          <Card className="p-4 text-center">
            <AlertTriangle size={20} className="mx-auto mb-1 text-red-500" />
            <p className="text-xl font-bold dark:text-gray-100">{stats.pendingReports}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.stats.pending')}</p>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl mb-6">
        {['pending', 'resolved', 'rejected'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${
              filter === f
                ? 'bg-white dark:bg-gray-700 text-rose-600 dark:text-rose-400 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {f === 'pending'
              ? t('admin.filters.pending')
              : f === 'resolved'
                ? t('admin.filters.resolved')
                : t('admin.filters.rejected')}
          </button>
        ))}
      </div>

      {/* Liste signalements */}
      {error ? (
        <ErrorState onRetry={() => window.location.reload()} />
      ) : loading ? (
        <Loading msg={t('admin.loadingReports')} />
      ) : reports.length === 0 ? (
        <Empty
          icon={Check}
          title={filter === 'pending' ? t('admin.emptyPending') : t('admin.emptyOther')}
          desc={t('admin.allGood')}
        />
      ) : (
        <div className="space-y-3">
          {reports.map((report) => {
            const reporter = profiles[report.reporterId];
            const reported = profiles[report.reportedId];
            return (
              <Card key={report.id} className="p-4">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge c="orange">
                        {t(REASON_KEYS[report.reason] || 'admin.reasons.other')}
                      </Badge>
                      {report.type === 'annonce' ? (
                        <Badge c="blue" data-testid="report-type-badge">
                          {t('admin.report.typeAnnonce')}
                        </Badge>
                      ) : (
                        <Badge c="gray" data-testid="report-type-badge">
                          {t('admin.report.typeProfile')}
                        </Badge>
                      )}
                      <span className="text-xs text-gray-400">
                        <Clock size={12} className="inline mr-1" />
                        {report.createdAt?.toDate?.()
                          ? new Intl.DateTimeFormat('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            }).format(report.createdAt.toDate())
                          : '—'}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-sm mb-2 flex-wrap">
                      <div>
                        <span className="text-gray-400">{t('admin.report.by')}</span>
                        <span className="font-semibold dark:text-gray-200">
                          {reporter?.name || report.reporterId}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">{t('admin.report.against')}</span>
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          {reported?.name || report.reportedId}
                        </span>
                      </div>
                      {report.type === 'annonce' && report.annonceId && (
                        <button
                          onClick={() => navigate(`/annonces/${report.annonceId}`)}
                          className="text-blue-500 hover:underline font-medium"
                        >
                          {t('admin.report.seeAnnonce')}
                        </button>
                      )}
                    </div>

                    {report.comment && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                        {report.comment}
                      </p>
                    )}
                  </div>

                  {filter === 'pending' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <Btn
                        v="ghost"
                        size="sm"
                        icon={Check}
                        loading={actionLoading === report.id}
                        onClick={() => handleResolve(report.id, 'resolved')}
                        className="text-green-600"
                      >
                        {t('admin.actions.resolve')}
                      </Btn>
                      <Btn
                        v="ghost"
                        size="sm"
                        icon={Ban}
                        loading={actionLoading === report.id}
                        onClick={() => handleResolve(report.id, 'rejected')}
                        className="text-red-500"
                      >
                        {t('admin.actions.reject')}
                      </Btn>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Feedbacks */}
      {!loading && !error && (
        <div className="mt-8">
          <h2 className="text-lg font-bold dark:text-gray-100 flex items-center gap-2 mb-4">
            <MessageSquarePlus size={20} className="text-purple-500" />
            {t('admin.feedbacks.title')}
            {newFeedbackCount > 0 && (
              <span className="bg-purple-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {newFeedbackCount}
              </span>
            )}
          </h2>
          {feedbacks.length === 0 ? (
            <p className="text-gray-400 text-sm">{t('admin.feedbacks.empty')}</p>
          ) : (
            <div className="space-y-3">
              {feedbacks.map((fb) => {
                const isExpanded = expandedFeedback === fb.id;
                return (
                  <Card key={fb.id} className="p-4">
                    <div
                      className="flex items-start justify-between gap-3 cursor-pointer"
                      onClick={() => setExpandedFeedback(isExpanded ? null : fb.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge
                            c={
                              fb.type === 'bug' ? 'red' : fb.type === 'suggestion' ? 'blue' : 'gray'
                            }
                          >
                            {t(`feedback.types.${fb.type}`)}
                          </Badge>
                          <Badge
                            c={
                              fb.status === 'new'
                                ? 'orange'
                                : fb.status === 'read'
                                  ? 'blue'
                                  : 'green'
                            }
                          >
                            {t(`admin.feedbacks.status.${fb.status}`)}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            <Clock size={12} className="inline mr-1" />
                            {fb.createdAt?.toDate?.()
                              ? new Intl.DateTimeFormat('fr-FR', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                }).format(fb.createdAt.toDate())
                              : '—'}
                          </span>
                        </div>
                        <p className="text-sm font-semibold dark:text-gray-200">
                          {fb.userName || fb.userId}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {fb.description}
                        </p>
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={16} className="text-gray-400 flex-shrink-0 mt-1" />
                      ) : (
                        <ChevronDown size={16} className="text-gray-400 flex-shrink-0 mt-1" />
                      )}
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t dark:border-gray-700 space-y-2">
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {fb.description}
                        </p>
                        <div className="text-xs text-gray-400 space-y-1">
                          <p>
                            {t('admin.feedbacks.page')}: {fb.page}
                          </p>
                          <p>
                            {t('admin.feedbacks.userAgent')}: {fb.userAgent}
                          </p>
                          {fb.userEmail && <p>Email: {fb.userEmail}</p>}
                        </div>
                        <div className="flex gap-2 pt-2">
                          {fb.status === 'new' && (
                            <Btn
                              v="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFeedbackStatus(fb.id, 'read');
                              }}
                            >
                              {t('admin.feedbacks.markRead')}
                            </Btn>
                          )}
                          {fb.status !== 'resolved' && (
                            <Btn
                              v="ghost"
                              size="sm"
                              icon={Check}
                              className="text-green-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFeedbackStatus(fb.id, 'resolved');
                              }}
                            >
                              {t('admin.feedbacks.markResolved')}
                            </Btn>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
